import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'results-glossary',
    category: 'results',
    title: 'Glossar — Energiebegriffe in einfacher Sprache',
    summary: 'Alle Begriffe, die in Ergebnisdiagrammen und KPI-Karten erscheinen, an einem Ort.',
    body: (
      <>
        <p>Die anderen Artikel in diesem Abschnitt verweisen hierher. Setzen Sie ein Lesezeichen als schnelle Referenz.</p>

        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Begriff</th><th>Bedeutung</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>kWh</strong> (Kilowattstunde)</td>
              <td>Eine Einheit der <em>Energie</em>. Eine kWh ≈ ein Heizlüfter, der eine Stunde läuft. Stromrechnungen werden in kWh angegeben.</td>
            </tr>
            <tr>
              <td><strong>kW</strong> (Kilowatt)</td>
              <td>Eine Einheit der <em>Leistung</em> — wie schnell Energie gerade verbraucht wird. Ein 2-kW-Wasserkocher, der eine halbe Stunde läuft, verbraucht 1 kWh.</td>
            </tr>
            <tr>
              <td><strong>Spitzenlast</strong></td>
              <td>Die höchste Leistung (kW), die in einem Zeitraum entnommen wird. Netzentgelte richten sich oft nach dieser Zahl.</td>
            </tr>
            <tr>
              <td><strong>Erzeugung</strong></td>
              <td>Strom, der von Solarmodulen, Windturbinen usw. erzeugt wird.</td>
            </tr>
            <tr>
              <td><strong>Last / Verbrauch</strong></td>
              <td>Von Gebäuden und Geräten verbrauchter Strom.</td>
            </tr>
            <tr>
              <td><strong>Eigenverbrauch (%)</strong></td>
              <td>Von der gesamten <em>erzeugten</em> Energie: welchen Anteil haben Sie selbst verbraucht
                (statt ihn einzuspeisen)? Hoch = Sie verschwenden weniger Ihrer Erzeugung.</td>
            </tr>
            <tr>
              <td><strong>Autarkie (%)</strong></td>
              <td>Von der gesamten <em>verbrauchten</em> Energie: welchen Anteil hat Ihre eigene Erzeugung gedeckt?
                Hoch = Sie sind weniger vom Netz abhängig.</td>
            </tr>
            <tr>
              <td><strong>Gemeinsam genutzte Energie</strong> <em>(Konzept)</em></td>
              <td>Energie, die von einem Gemeinschaftsmitglied erzeugt und innerhalb derselben Stunde von
                einem anderen Mitglied verbraucht wird. Viele nationale Regelungen belohnen dies. Die Plattform zeigt dies derzeit
                nicht als eigenen KPI — es spiegelt sich indirekt im Eigenverbrauch und
                im Energiebilanz-Diagramm wider.</td>
            </tr>
            <tr>
              <td><strong>Bezug / Einspeisung</strong></td>
              <td>Bezug = aus dem Netz entnommener Strom. Einspeisung = in das Netz eingespeister Strom.</td>
            </tr>
            <tr>
              <td><strong>Tarif</strong></td>
              <td>Der Strompreis in €/kWh, der oft stündlich variiert.</td>
            </tr>
            <tr>
              <td><strong>Ladezustand (SOC)</strong></td>
              <td>Wie voll eine Batterie ist: 0 % (leer) bis 100 % (voll).</td>
            </tr>
            <tr>
              <td><strong>NPV</strong> (Kapitalwert)</td>
              <td>Der Gesamtwert einer Investition über ihre Laufzeit, in heutigem Geldwert. <strong>Positiv = rentabel</strong>; negativ = Verlust.</td>
            </tr>
            <tr>
              <td><strong>IRR</strong> (Interner Zinsfuß) <em>(Konzept)</em></td>
              <td>Die jährliche prozentuale Rendite der Investition. Die CBA-Ergebnisse der Plattform
                zeigen derzeit NPV, BCR, Amortisationszeit, Jahreseinsparungen und CO₂ — nicht direkt IRR — aber
                der Begriff erscheint in der Beschreibung des Tools.</td>
            </tr>
            <tr>
              <td><strong>BCR</strong> (Nutzen-Kosten-Verhältnis)</td>
              <td>Gesamtnutzen geteilt durch Gesamtkosten. Über 1 = die Investition amortisiert sich.</td>
            </tr>
            <tr>
              <td><strong>Amortisationszeit (Jahre)</strong></td>
              <td>Wie viele Jahre Einsparungen benötigt werden, um die Anfangskosten zu decken.</td>
            </tr>
            <tr>
              <td><strong>MAPE</strong> (Mittlerer absoluter prozentualer Fehler)</td>
              <td>Prognosefehler. Niedriger ist besser — z. B. bedeutet ein MAPE von 10 %, dass Vorhersagen
                im Durchschnitt um 10 % abweichen.</td>
            </tr>
            <tr>
              <td><strong>GHI</strong></td>
              <td>Globale Horizontalstrahlung — gesamte Sonneneinstrahlung auf eine horizontale Fläche, in W/m². Der wichtigste Wettereingangsparameter für die Solarprognose.</td>
            </tr>
          </tbody>
        </Table>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'results-forecast',
    category: 'results',
    title: 'Das Prognosediagramm lesen',
    summary: 'Was die Linie, das schattierte Band und die Genauigkeitszahl bedeuten.',
    body: (
      <>
        <p>
          Ein Prognoseergebnis besteht aus drei Teilen: einer <strong>Verlaufslinie</strong>, einer
          {' '}<strong>Vorhersagelinie</strong> und einem <strong>schattierten Band</strong> um die
          Vorhersage, das den Unsicherheitsbereich anzeigt.
        </p>
        <ScreenshotPlaceholder
          img="forecast-results.png"
          description="A finished forecast — line for past data, a forecast line, and a shaded band around the prediction. A MAPE value and a Confidence level badge (LOW / MEDIUM / MEDIUM-HIGH / HIGH) are shown alongside."
          source="Platform — Forecasting page, after a successful run. Capture the result panel on the right: chart + the MAPE/Confidence indicators. (A community with uploaded load data is needed.)"
          caption="Ein typisches Prognoseergebnis."
        />

        <ul>
          <li><strong>Verlaufslinie</strong> — was tatsächlich passiert ist. Verwenden Sie sie als Plausibilitätsprüfung, ob die Eingabedaten korrekt aussehen.</li>
          <li><strong>Vorhersagelinie</strong> — die beste Einzelschätzung des Modells für die Zukunft.</li>
          <li><strong>Schattiertes Band</strong> — ein Bereich, in dem der tatsächliche Wert wahrscheinlich liegen wird. Ein <em>schmales</em> Band bedeutet, dass das Modell sicher ist; ein <em>breites</em> Band bedeutet Unsicherheit (oft bei langen Zeithorizonten oder wenigen Eingabedaten).</li>
        </ul>

        <h5 className="mt-3">Zwei Genauigkeitsindikatoren</h5>
        <p>
          Die Plattform zeigt zwei zusammenfassende Indikatoren neben jeder Prognose — lesen Sie sie gemeinsam:
        </p>

        <p className="mb-1"><strong>1. Konfidenzniveau</strong> — ein einzelnes Badge mit einem von vier Werten:</p>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Badge</th><th>Bedeutung</th></tr>
          </thead>
          <tbody>
            <tr><td><strong>HIGH</strong></td><td>Ausreichend saubere Eingabedaten; Ergebnisse für operative Entscheidungen geeignet.</td></tr>
            <tr><td><strong>MEDIUM-HIGH</strong></td><td>Gut. Für die Planung verwendbar.</td></tr>
            <tr><td><strong>MEDIUM</strong></td><td>Brauchbare, aber unvollkommene Eingaben — vor Entscheidungen bei Grenzfällen überprüfen.</td></tr>
            <tr><td><strong>LOW</strong></td><td>Begrenzte Eingabedaten oder schwaches Signal. Prognose als indikativ betrachten; mehr Verlaufsdaten hinzufügen.</td></tr>
          </tbody>
        </Table>

        <p className="mt-3 mb-1"><strong>2. MAPE</strong> (Mittlerer absoluter prozentualer Fehler) — die durchschnittliche Abweichung zwischen Vorhersage und Realität in %. Grobe Branchenorientierung:</p>
        <Table bordered size="sm" className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>MAPE</th><th>Bedeutung</th></tr>
          </thead>
          <tbody>
            <tr><td>&lt; 10%</td><td>Sehr gut.</td></tr>
            <tr><td>10 – 20%</td><td>Gut. Für Planung und Optimierung verwendbar.</td></tr>
            <tr><td>20 – 35%</td><td>Akzeptabel für die Frühphase. Daten oder Wetterdaten hinzufügen, um zu verbessern.</td></tr>
            <tr><td>&gt; 35%</td><td>Als illustrativ betrachten. Siehe <ArticleLink to="tb-forecast-flat">„Meine Prognose sieht flach oder falsch aus"</ArticleLink>.</td></tr>
          </tbody>
        </Table>

        <Tip>
          Solarprognosen sind in der Regel genauer als Lastprognosen, da Sonnenlicht besser vorhersagbar ist.
          Das Hinzufügen einer Wetterdatei senkt den solaren MAPE weiter — siehe
          {' '}<ArticleLink to="data-weather">Wetterdateien</ArticleLink>.
        </Tip>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'results-optimisation-kpis',
    category: 'results',
    title: 'Die Optimierungs-KPIs lesen',
    summary: 'Was jede Karte in der KPI-Zusammenfassung nach einem Optimierungslauf bedeutet.',
    body: (
      <>
        <p>
          Nach einem Optimierungslauf erscheint eine <strong>KPI-Zusammenfassung</strong> oberhalb der Diagramme. Die
          angezeigten Karten hängen davon ab, ob Ihre Gemeinschaft eine Batterie hat und ob ein Tarif
          verfügbar war, aber der Standardsatz ist unten aufgeführt.
        </p>
        <ScreenshotPlaceholder
          img="kpi-cards.png"
          description="The KPI Summary panel that appears after running an optimisation — cards for Daily Cost, Self-Sufficiency, Self-Consumption, Grid Import, Grid Import Cost, Grid Export Revenue, Peak Import and CO2 Avoided."
          source="Platform — Optimization page (titled 'Modelling'), after a successful run. Capture the KPI Summary row that appears above the charts."
          caption="Das KPI-Zusammenfassungspanel aus dem Optimierungsergebnis."
        />

        <h5 className="mt-3">Jeder KPI erklärt</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>KPI</th><th>Was er Ihnen sagt</th><th>Wie Sie ihn verbessern</th></tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Tageskosten</strong></td>
              <td>Gesamtkosten für den Betrieb der Gemeinschaft am optimierten Tag (Bezug abzüglich Einspeisungserlös).</td>
              <td>Bessere Tarifsignale, mehr PV, eine auf die Spitzenstunden abgestimmte Batterie.</td>
            </tr>
            <tr>
              <td><strong>Autarkie (%)</strong></td>
              <td>„Von der verbrauchten Energie: wie viel stammte aus meiner eigenen Erzeugung?"</td>
              <td>Mehr PV hinzufügen; Verbrauch reduzieren; Speicher hinzufügen.</td>
            </tr>
            <tr>
              <td><strong>Eigenverbrauch (%)</strong></td>
              <td>„Von der erzeugten Energie: wie viel habe ich selbst verbraucht statt eingespeist?"</td>
              <td>Batterie hinzufügen; flexible Lasten in Sonnenstunden verlagern.</td>
            </tr>
            <tr>
              <td><strong>Netzbezug (kWh)</strong></td>
              <td>Gesamte an diesem Tag aus dem Netz bezogene Energie.</td>
              <td>Mehr PV und Speicher; flexible Lasten in Solarstunden.</td>
            </tr>
            <tr>
              <td><strong>Netzbezugskosten</strong></td>
              <td>Was der Bezug in Währung gekostet hat. Kombination aus Menge × Tarif.</td>
              <td>Bezug in Spitzenpreiszeiten vermeiden; aus günstigeren Stunden vorladen.</td>
            </tr>
            <tr>
              <td><strong>Netzeinspeisungserlös</strong></td>
              <td>Was für die Einspeisung gezahlt wurde.</td>
              <td>Hängt von Ihrem Einspeisevergütungsmodell ab; kein Ziel an sich, wenn Eigenverbrauch bevorzugt wird.</td>
            </tr>
            <tr>
              <td><strong>Spitzenbezug (kW)</strong></td>
              <td>Höchste stündliche Netzentnahme während des Tages. Netzentgelte richten sich oft danach.</td>
              <td>Lasten zeitlich verteilen; Batterie zur Spitzenlastkappung nutzen.</td>
            </tr>
            <tr>
              <td><strong>Vermiedene CO2 (kg)</strong></td>
              <td>Vermiedene Emissionen gegenüber der gleichen Energie aus dem Netz. Durch selbstverbrauchte PV-/Windenergie bedingt.</td>
              <td>Eigenverbrauch maximieren; PV auf die Tagesverbrauchslast abstimmen.</td>
            </tr>
          </tbody>
        </Table>

        <h5 className="mt-3">Eigenverbrauch vs. Autarkie — das verwechseltste Paar</h5>
        <p>Sie klingen ähnlich, beantworten aber entgegengesetzte Fragen:</p>
        <ul>
          <li><strong>Eigenverbrauch</strong> bezieht sich auf <em>Ihre Erzeugung</em>: wie viel von dem, was Sie erzeugt haben, haben Sie verbraucht?</li>
          <li><strong>Autarkie</strong> bezieht sich auf <em>Ihren Verbrauch</em>: wie viel von dem, was Sie verbraucht haben, haben Sie selbst erzeugt?</li>
        </ul>

        <h5 className="mt-3">Plausibilitätsbereiche</h5>
        <ul>
          <li>Eine Gemeinschaft mit nur bescheidener Dach-PV und ohne Batterie: Eigenverbrauch 60–90 %, Autarkie 10–25 %.</li>
          <li>Eine Gemeinschaft mit einer großen Solaranlage und einer Batterie: Autarkie kann 50 % übersteigen, Eigenverbrauch 80–95 %.</li>
          <li>Autarkie über ca. 90 % bedeutet, dass Sie kaum beziehen — überprüfen Sie, ob die Lastdatei nicht unterschätzt ist.</li>
        </ul>
        <Warn>
          Wenn ein KPI unrealistisch gut aussieht (z. B. 99 % Autarkie mit nur bescheidener PV), siehe
          {' '}<ArticleLink to="tb-kpi-sanity-check">„Meine KPIs sehen zu gut oder zu schlecht aus"</ArticleLink>.
        </Warn>
      </>
    ),
  },
]
